import { useMemo, useState, useRef, useLayoutEffect } from "react";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { PolicyAction, hasPermission } from "@vmosolutions/oc-interfaces";
// Redux
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ITableData, tableData } from "./crewTableDefinition";
import { Grid, Typography, Button } from "@mui/material"; // Typography, Button
import { TextField } from "src/components/FormikComponents/TextField";
import { ICrewInPackage } from "./CrewGroupsField";
import { CrewDisplayMode } from "./CrewDisplayMode";
import _ from "lodash";

export interface IExtendedCrewInPackage extends ICrewInPackage {
  status?: "removed" | "nochange" | "added"; // property added to table data in order to show crew changes that will take place is crew is rebuilt using selected groups.
}

interface ICrewBidTableProps {
  crewInPackage: IExtendedCrewInPackage[];
  path: string[];
  id: string;
  crewAdded: string[];
  crewRemoved: string[];
}

export interface ITableColumn {
  id: string;
  value: string;
  index: number;
  numeric: boolean;
  label: string;
  type: string;
  width: number;
  visible: boolean;
  filter: boolean;
}

export const CrewBidTable = (props: ICrewBidTableProps): JSX.Element | null => {
  const { crewInPackage, id, path, crewAdded, crewRemoved } = props;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);

  // Crew verification filters.
  const [firstNameFilter, setFirstNameFilter] = useState<string | null>(null);
  const [lastNameFilter, setLastNameFilter] = useState<string | null>(null);
  const [employeeIdFilter, setEmployeeIdFilter] = useState<string | null>(null);
  const [displayMode, setDisplayMode] = useState<"removed" | "all" | "added">("all");

  // Create array of crew members that exist in any of the selected crew groups.
  const filteredCrewInPackage = useMemo(() => {
    if (crewInPackage?.length > 0) {
      // Add status information to crew package.
      const CrewWithStatus: IExtendedCrewInPackage[] = crewInPackage?.map((c) => {
        // const isAdded = crewAdded?.includes(c?.userId); done in parent component.
        const isRemoved = crewRemoved?.includes(c?.userId);
        return !c?.status ? { ...c, status: isRemoved ? "removed" : "nochange" } : c;
      });
      return CrewWithStatus.filter((c) => {
        // Apply filters if user has assigned them.
        const includesFirstNameFilter =
          !firstNameFilter ||
          (firstNameFilter?.length >= 0 &&
            c?.crewMember?.firstName?.toLowerCase().includes(firstNameFilter.toLowerCase()));
        const includesLastNameFilter =
          !lastNameFilter ||
          (lastNameFilter?.length >= 0 &&
            c?.crewMember?.lastName?.toLowerCase().includes(lastNameFilter.toLowerCase()));
        const includesEmployeeIdFilter =
          !employeeIdFilter ||
          (employeeIdFilter?.length >= 0 &&
            c?.crewMember?.employeeId?.toLowerCase().includes(employeeIdFilter.toLowerCase()));
        const matchesDisplayMode = c?.status === displayMode || displayMode === "all";
        return includesFirstNameFilter && includesLastNameFilter && includesEmployeeIdFilter && matchesDisplayMode;
      });
    }
    return [];
  }, [crewInPackage, firstNameFilter, lastNameFilter, employeeIdFilter, displayMode, crewRemoved]);

  // Add crew data to the table data.
  const tableContent = useMemo(() => {
    return tableData?.map((td) => ({ ...td, data: filteredCrewInPackage }));
  }, [filteredCrewInPackage]);

  // check policies to ensure user is allowed to see this data.
  const allowedTableData: ITableData[] = useMemo(
    () =>
      tableContent.filter((data: ITableData) => hasPermission(policies, data.permissionResource, PolicyAction.Read)),
    [tableContent, policies]
  );

  const [containerHeight, setContainerHeight] = useState<number>(0);
  useLayoutEffect(() => {
    setTimeout(() => {
      const container = tableContainerRef?.current?.getBoundingClientRect();
      const tableHeight = container?.height ? Math.round(container?.height) - 150 : 0;
      // console.log(tableHeight);
      setContainerHeight(tableHeight);
    }, 100);
  }, [path]);

  //if some how you open this window and have no allowed table data
  if (!allowedTableData.length) {
    return null;
  }
  return (
    <Grid container direction="column" style={{ height: "calc(100% - 95px)" }} ref={tableContainerRef}>
      <FilterSearchTable
        id={id}
        path={path}
        className="crew_on_bidpackage_table"
        events={allowedTableData}
        parentId={mosaicPanel}
        uniqueKey={"userId"}
        allowMultiFilter={true}
        showDateRangeRRule={true}
        showSavedFilters={true}
        hideFocusSelected={true}
        hideSelectionCheckboxes={true}
        maxTableHeight={containerHeight}
        defaultRowsPerPage={25}
        optionalControls={
          <Grid container alignItems="center" style={{ marginLeft: 15, minWidth: 1080 }} wrap="nowrap">
            <Typography variant="caption" color="textPrimary">
              Filter Crew
            </Typography>
            <TextField
              className="field textfield surname_filter"
              name="crew_first_name_filter"
              label="First Name"
              value={firstNameFilter}
              onChange={(event) => setFirstNameFilter(event?.target?.value ?? null)}
              type="text"
            />
            <TextField
              className="field textfield givenname_filter"
              name="crew_last_name_filter"
              label="Last Name"
              value={lastNameFilter}
              onChange={(event) => setLastNameFilter(event?.target?.value ?? null)}
              type="text"
            />
            <TextField
              className="field textfield employee_id_filter"
              name="crew_employee_id_filter"
              label="Employee Id"
              value={employeeIdFilter}
              onChange={(event) => setEmployeeIdFilter(event?.target?.value ?? null)}
              type="text"
            />
            <CrewDisplayMode
              displayMode={displayMode}
              setDisplayMode={setDisplayMode}
              addedCount={crewAdded?.length}
              removedCount={crewRemoved?.length}
            />
            <Button
              className="refresh_btn"
              variant="outlined"
              onClick={() => {
                setFirstNameFilter(null);
                setLastNameFilter(null);
                setEmployeeIdFilter(null);
                setDisplayMode("all");
              }}
            >
              Reset All Filters
            </Button>
          </Grid>
        }
      />
    </Grid>
  );
};
